import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Upload,
  Download,
  Loader,
  AlertCircle,
  Info,
  FileText,
  ArrowLeft,
  Users,
  X,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Eye,
  Sparkles,
  Send,
  Calendar,
  Phone,
  Image as ImageIcon,
  Video,
  File,
} from "lucide-react";
import {
  getParameterFormat,
  generateCSVHeaders as sharedGenerateCSVHeaders,
  validateTemplateCompatibility as sharedValidateTemplateCompatibility,
  sampleValueForHeader,
} from "../utils/campaignTemplateUtils";
import moment from "moment-timezone";
import TemplatePreview from "./templateCreator/TemplatePreview";
import { transformDuplicateTemplateToCreatorState } from "../utils/templateDataTransformers";
import { applyCampaignVarsToPreviewState } from "../utils/campaignTemplatePreview";
import { getTemplates } from "../apis/template";
import { getSegments } from "../apis/segments";
import { listContactLists } from "../apis/contactLists";
import VariableMappingPanel from "./campaigns/VariableMappingPanel";
import { sendTestTemplate } from "../apis/campaign";
import { usePhoneAccount } from "../contexts/PhoneAccountContext";
import { COUNTRY_CODES } from "../constants/countryCodes";
import api from "../services/api";
import SearchableSelect from "./ui/SearchableSelect";

// Meta's per-format limits for media template headers. Keep these in sync
// with server/utils/campaignHeaderMedia.js — both are enforced.
const META_HEADER_MEDIA_LIMITS = {
  IMAGE: {
    label: "Image",
    maxBytes: 5 * 1024 * 1024,
    accept: ".jpg,.jpeg,.png,image/jpeg,image/png",
    extensions: [".jpg", ".jpeg", ".png"],
    mimeTypes: ["image/jpeg", "image/png"],
    icon: ImageIcon,
  },
  VIDEO: {
    label: "Video",
    maxBytes: 16 * 1024 * 1024,
    accept: ".mp4,.3gp,video/mp4,video/3gpp",
    extensions: [".mp4", ".3gp"],
    mimeTypes: ["video/mp4", "video/3gpp"],
    icon: Video,
  },
  DOCUMENT: {
    label: "Document",
    maxBytes: 100 * 1024 * 1024,
    accept:
      ".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain",
    extensions: [".pdf", ".doc", ".docx", ".ppt", ".pptx", ".xls", ".xlsx", ".txt"],
    mimeTypes: [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-powerpoint",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "text/plain",
    ],
    icon: File,
  },
};

const formatBytes = (bytes) => {
  if (bytes >= 1024 * 1024) return `${Math.round(bytes / (1024 * 1024))}MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)}KB`;
  return `${bytes}B`;
};

const validateHeaderMediaFile = (file, format) => {
  const limits = META_HEADER_MEDIA_LIMITS[format];
  if (!limits) {
    return { valid: false, error: "This template header does not accept uploaded media." };
  }
  const lowerName = (file.name || "").toLowerCase();
  const ext = lowerName.includes(".") ? lowerName.slice(lowerName.lastIndexOf(".")) : "";
  const mimeOk = file.type ? limits.mimeTypes.includes(file.type) : false;
  const extOk = ext ? limits.extensions.includes(ext) : false;
  if (!mimeOk && !extOk) {
    return {
      valid: false,
      error: `${limits.label} header only accepts ${limits.extensions.join(", ")}`,
    };
  }
  if (file.size > limits.maxBytes) {
    return {
      valid: false,
      error: `${limits.label} exceeds Meta's ${formatBytes(limits.maxBytes)} limit (file is ${formatBytes(file.size)})`,
    };
  }
  return { valid: true, error: null };
};

const CreateCampaignForm = ({ onCancel, onSubmit }) => {
  const { selectedPhoneId, selectedPhoneAccount } = usePhoneAccount();
  const [templates, setTemplates] = useState([]);
  const [segments, setSegments] = useState([]);
  // Centralized contact lists (replaces the legacy `segments` UI source).
  // We keep `segments` state for backward compatibility but ignore it
  // when picking a recipient list.
  const [contactLists, setContactLists] = useState([]);
  // Per-template-variable mapping spec sent to the backend resolver.
  // Used in BOTH CSV mode (csv_column source) and Contact-List mode
  // (field / custom_field / computed sources). Same panel, two source
  // catalogs.
  const [variableMappings, setVariableMappings] = useState({});
  // CSV column headers, captured on upload, fed to the mapping panel.
  const [csvColumns, setCsvColumns] = useState([]);
  // Per-variable inline errors echoed from the server's coverage check.
  // Keyed by mapping key (phone, country_code, body_1, …).
  const [variableMappingErrors, setVariableMappingErrors] = useState({});
  const [phoneAccounts, setPhoneAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPhoneAccounts, setLoadingPhoneAccounts] = useState(false);
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [selectedTemplateForPreview, setSelectedTemplateForPreview] = useState(null);
  const [originalTemplate, setOriginalTemplate] = useState(null);
  const [useSegment, setUseSegment] = useState(false);
  const [templateValidationError, setTemplateValidationError] = useState("");
  const [activePreviewIndex, setActivePreviewIndex] = useState(0);
  
  const lastLoadedPhoneIdRef = useRef(null);

  const [campaignData, setCampaignData] = useState({
    name: "",
    description: "",
    templateIds: [],
    whatsapp_account_id: "",
    segment_id: "",
    use_segment: false,
    csvFile: null,
    csvData: [],
    sendOption: "now",
    scheduledDate: "",
    scheduledTime: "",
    scheduledTimestamp: "",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });

  const [errors, setErrors] = useState({});
  const [csvValidationErrors, setCsvValidationErrors] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [defaultValues, setDefaultValues] = useState({});
  const [csvContactCount, setCsvContactCount] = useState(0);
  const [selectedSegmentContactCount, setSelectedSegmentContactCount] = useState(0);
  
  // Test feature states
  const [showTestSection, setShowTestSection] = useState(false);
  const [testCountryCode, setTestCountryCode] = useState("+91");
  const [testPhoneNumber, setTestPhoneNumber] = useState("");
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [testResult, setTestResult] = useState(null);
  // Per-variable sample values used ONLY for the test send/preview. Kept
  // separate from `defaultValues` so test inputs never leak into the
  // real campaign payload. Keyed by param key (body_1, header_1, named, …).
  const [testValues, setTestValues] = useState({});

  // Header media override (image/video/document). Applied to every contact
  // in the campaign, overriding any per-row CSV value.
  const [headerMediaFile, setHeaderMediaFile] = useState(null);
  const [headerMediaPreviewUrl, setHeaderMediaPreviewUrl] = useState("");
  const [headerMediaError, setHeaderMediaError] = useState("");
  const headerMediaInputRef = useRef(null);


  useEffect(() => {
    loadPhoneAccounts();
  }, []);

  useEffect(() => {
    const phoneIdToUse = campaignData.whatsapp_account_id || selectedPhoneId;
    if (phoneIdToUse) {
      loadSegments();
      if (!campaignData.whatsapp_account_id && selectedPhoneId) {
        handleInputChange("whatsapp_account_id", selectedPhoneId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPhoneId, campaignData.whatsapp_account_id]);

  // Single effect for templates: load when phone changes, clear selection (no loadTemplates in handleInputChange)
  useEffect(() => {
    const phoneId = campaignData.whatsapp_account_id || selectedPhoneId;
    if (!phoneId) return;

    // Skip if we already loaded for this phone (prevents duplicate calls)
    if (lastLoadedPhoneIdRef.current === String(phoneId)) return;
    lastLoadedPhoneIdRef.current = String(phoneId);

    setSelectedTemplates([]);
    setSelectedTemplateForPreview(null);
    setOriginalTemplate(null);
    setDefaultValues({});
    setTemplateValidationError("");
    setCampaignData((prev) => (prev.templateIds?.length ? { ...prev, templateIds: [] } : prev));
    loadTemplates(phoneId);
  }, [campaignData.whatsapp_account_id, selectedPhoneId]);

  // Update contact-list count when a list is selected. We treat
  // campaignData.segment_id as the chosen contact_list_id while the
  // form is in "Use Contact List" mode (single field, two semantics —
  // kept this way to minimise blast radius on the rest of the form).
  useEffect(() => {
    if (useSegment && campaignData.segment_id && contactLists.length > 0) {
      const chosen = contactLists.find((l) => String(l.id) === String(campaignData.segment_id));
      setSelectedSegmentContactCount(chosen?.member_count ?? chosen?.contact_count ?? 0);
    } else if (!useSegment) {
      setSelectedSegmentContactCount(0);
    }
  }, [useSegment, campaignData.segment_id, contactLists]);

  // Read the template's header format (IMAGE / VIDEO / DOCUMENT / TEXT / NONE).
  const getTemplateHeaderFormat = (template) => {
    if (!template) return "NONE";
    let json = template.template_json || {};
    if (typeof json === "string") {
      try {
        json = JSON.parse(json);
      } catch {
        json = {};
      }
    }
    const components = json.components || [];
    const headerComp = components.find((c) => c.type === "HEADER");
    if (headerComp?.format) return String(headerComp.format).toUpperCase();
    if (template.headerFormat) return String(template.headerFormat).toUpperCase();
    if (template.headerType) return String(template.headerType).toUpperCase();
    return "NONE";
  };

  const headerMediaFormat = getTemplateHeaderFormat(originalTemplate);
  const headerMediaLimits = META_HEADER_MEDIA_LIMITS[headerMediaFormat] || null;

  // Drop any previously-selected media file when the template (or its header
  // type) changes — e.g. switching from an IMAGE template to a TEXT one.
  useEffect(() => {
    if (!headerMediaLimits) {
      if (headerMediaFile) setHeaderMediaFile(null);
      if (headerMediaPreviewUrl) {
        URL.revokeObjectURL(headerMediaPreviewUrl);
        setHeaderMediaPreviewUrl("");
      }
      if (headerMediaError) setHeaderMediaError("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerMediaFormat]);

  // Clean up the object URL on unmount.
  useEffect(() => {
    return () => {
      if (headerMediaPreviewUrl) URL.revokeObjectURL(headerMediaPreviewUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getQualityBadge = (quality) => {
    if (quality == null || quality === "") {
      return { label: "—", class: "bg-gray-100 text-gray-500 border-gray-200" };
    }
    const q = String(quality).toUpperCase().trim();
    const configs = {
      GREEN: { label: "GREEN", class: "bg-emerald-100 text-emerald-700 border-emerald-200" },
      YELLOW: { label: "YELLOW", class: "bg-amber-100 text-amber-700 border-amber-200" },
      RED: { label: "RED", class: "bg-red-100 text-red-700 border-red-200" },
    };
    return configs[q] || { label: q || "—", class: "bg-gray-100 text-gray-600 border-gray-200" };
  };

  const loadTemplates = async (phoneId) => {
    if (!phoneId) return;
    try {
      setLoading(true);
      const response = await getTemplates({
        limit: 1000,
        offset: 0,
        phoneId,
        status: "approved",
      });

      const rawTemplates = response.data.metaTemplates || response.data.templates || [];
      const approved = rawTemplates.filter(
        (t) => (t.status === "approved" || t.status === "APPROVED")
      );
      const notAuth = approved.filter(
        (t) => (t.category || "").toLowerCase() !== "authentication"
      );
      const withQuality = notAuth.filter((t) => {
        const q = (t.quality_score || "").toString().toUpperCase();
        return q !== "RED";
      });
      setTemplates(withQuality.length > 0 ? withQuality : notAuth);
    } catch (err) {
      console.error("Failed to load templates:", err);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const loadSegments = async () => {
    // Pulls centralized contact lists scoped to the currently-selected
    // WABA phone. Falls back to the legacy /api/segments endpoint only
    // if the new endpoint is unreachable, so older deployments keep
    // working.
    const phoneId = campaignData.whatsapp_account_id || selectedPhoneId;
    if (!phoneId) {
      setContactLists([]);
      return;
    }
    try {
      const response = await listContactLists({
        phone_id: phoneId,
        status: "active",
        limit: 200,
      });
      const lists = Array.isArray(response.data?.lists) ? response.data.lists : [];
      setContactLists(lists);
    } catch (err) {
      console.error("Failed to load contact lists:", err);
      try {
        const fallback = await getSegments({ status: "active" });
        setSegments(fallback.data.segments || []);
      } catch (legacyErr) {
        console.error("Legacy segments fallback also failed:", legacyErr);
      }
    }
  };

  const loadPhoneAccounts = async () => {
    try {
      setLoadingPhoneAccounts(true);
      const response = await api.get("/api/whatsapp-accounts", { params: { section: "campaigns" } });
      const data = response.data;
      const accounts = Array.isArray(data?.data) ? data.data : [];
      const flattened = [];
      accounts.forEach((account) => {
        if (
          account.phone_numbers &&
          Array.isArray(account.phone_numbers) &&
          account.phone_numbers.length > 0
        ) {
          account.phone_numbers.forEach((phone) => {
            flattened.push({
              id: account.id || account.waba_id,
              phoneId: phone.phone_id,
              phoneNumber: phone.phone_number,
              phoneName: phone.phone_name || phone.verified_name || "",
              wabaName: account.account_name || account.waba_name || account.business_name || "Unknown",
            });
          });
        } else if (account.phone_id) {
          flattened.push({
            id: account.waba_id || account.id,
            phoneId: account.phone_id,
            phoneNumber: account.phone_number,
            phoneName: account.verified_name || account.phone_name || "",
            wabaName: account.waba_name || account.account_name || account.business_name || "Unknown",
          });
        }
      });
      setPhoneAccounts(flattened);

      // Only set default when selectedPhoneId is NOT set - otherwise useEffect handles it
      if (flattened.length > 0 && !campaignData.whatsapp_account_id && !selectedPhoneId) {
        handleInputChange("whatsapp_account_id", flattened[0].phoneId);
      }
    } catch (err) {
      console.error("Failed to load phone accounts:", err);
      setPhoneAccounts([]);
    } finally {
      setLoadingPhoneAccounts(false);
    }
  };

  const handleInputChange = (field, value) => {
    setCampaignData((prev) => {
      const updated = { ...prev, [field]: value };
      
      if (field === "scheduledDate" || field === "scheduledTime") {
        const date = field === "scheduledDate" ? value : prev.scheduledDate;
        const time = field === "scheduledTime" ? value : prev.scheduledTime;
        
        if (date && time) {
          const timeWithSeconds = time.length === 5 ? `${time}:00` : time;
          updated.scheduledTimestamp = `${date} ${timeWithSeconds}`;
        } else {
          updated.scheduledTimestamp = "";
        }
      }
      
      // When phone account changes, clear selection - templates loaded by useEffect
      if (field === "whatsapp_account_id" && value) {
        updated.templateIds = [];
      }
      
      return updated;
    });
    
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
    
    if (field === "scheduledDate" || field === "scheduledTime") {
      if (errors.scheduledTimestamp) {
        setErrors((prev) => ({ ...prev, scheduledTimestamp: "" }));
      }
    }
  };

  const buildTemplatePreviewState = (template, variableValues = {}, headerMediaUrl = null) => {
    if (!template) return null;
    let state = transformDuplicateTemplateToCreatorState(template);
    if (headerMediaUrl) {
      state = { ...state, headerFilePreview: headerMediaUrl };
    }
    return applyCampaignVarsToPreviewState(state, variableValues);
  };

  const transformTemplateForPreview = (template) => {
    if (!template) return null;
    
    if (template.body && !template.template_json) {
      return {
        ...template,
        headerFormat: template.headerFormat || template.headerType || "NONE",
        buttons: template.buttons || [],
        variables: template.variables || [],
      };
    }
    
    let templateJson = template.template_json || {};
    if (typeof templateJson === 'string') {
      try {
        templateJson = JSON.parse(templateJson);
      } catch (e) {
        templateJson = {};
      }
    }
    
    const components = templateJson.components || [];
    
    if (components.length === 0) {
      return {
        ...template,
        body: template.body || '',
        footer: template.footer || '',
        headerFormat: template.headerFormat || template.headerType || "NONE",
        headerText: template.headerText || '',
        header_media_url: template.header_media_url || null,
        buttons: template.buttons || [],
        variables: template.variables || [],
      };
    }
    
    const bodyComponent = components.find(c => c.type === 'BODY');
    const body = bodyComponent?.text || template.body || '';
    
    const footerComponent = components.find(c => c.type === 'FOOTER');
    const footer = footerComponent?.text || template.footer || '';
    
    const headerComponent = components.find(c => c.type === 'HEADER');
    const headerFormat = headerComponent?.format?.toUpperCase() || template.headerFormat || template.headerType?.toUpperCase() || 'NONE';
    const headerText = headerComponent?.text || template.headerText || '';
    const headerMediaUrl = template.header_media_url || null;
    
    const buttonsComponent = components.find(c => c.type === 'BUTTONS');
    const rawButtons = buttonsComponent?.buttons || template.buttons || [];
    const buttons = rawButtons.map((btn, index) => ({
      id: index,
      type: btn.type,
      text: btn.text || btn.url || btn.phone_number || `Button ${index + 1}`,
      url: btn.url,
      phone_number: btn.phone_number,
    }));
    
    const variableMatches = body.match(/\{\{(\d+)\}\}/g) || [];
    const variables = variableMatches.map((match, index) => {
      const varNum = match.replace(/\{\{|\}\}/g, '');
      // body_text is an array of arrays, so we need body_text[0][varNum - 1]
      // varNum is a string, so we need to convert to number and subtract 1 for 0-based indexing
      const varIndex = parseInt(varNum, 10) - 1;
      const sampleValue = bodyComponent?.example?.body_text?.[0]?.[varIndex];
      return {
        id: index,
        name: varNum,
        sample: sampleValue || `Sample ${varNum}`,
      };
    });
    
    return {
      ...template,
      body,
      footer,
      headerFormat,
      headerText,
      header_media_url: headerMediaUrl,
      buttons,
      variables,
    };
  };

  const validateTemplateCompatibility = (selectedTemplateObjects) => {
    return sharedValidateTemplateCompatibility(selectedTemplateObjects);
  };

  const applyTemplateSelection = (newSelected) => {
    setSelectedTemplates(newSelected);
    handleInputChange("templateIds", newSelected);

    if (newSelected.length > 0) {
      const selectedTemplateObjects = newSelected
        .map(id => templates.find(t => String(t.id).trim() === String(id).trim()))
        .filter(Boolean);

      const validation = validateTemplateCompatibility(selectedTemplateObjects);
      setTemplateValidationError(validation.error);

      if (validation.valid && selectedTemplateObjects.length > 0) {
        const firstTemplate = selectedTemplateObjects[0];
        const transformedTemplate = transformTemplateForPreview(firstTemplate);
        transformedTemplate.selectedTemplates = selectedTemplateObjects.map(t => ({
          id: t.id,
          name: t.name,
          category: t.category,
        }));
        setSelectedTemplateForPreview(transformedTemplate);
        setOriginalTemplate(firstTemplate);

        const requiredParams = extractRequiredParameters(firstTemplate);
        const newDefaults = {};
        requiredParams.forEach(param => {
          newDefaults[param.key] = '';
        });
        setDefaultValues(newDefaults);
        setActivePreviewIndex(0);
      } else {
        setSelectedTemplateForPreview(null);
        setOriginalTemplate(null);
        setDefaultValues({});
      }
    } else {
      setSelectedTemplateForPreview(null);
      setOriginalTemplate(null);
      setDefaultValues({});
      setTemplateValidationError("");
    }
  };

  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];

      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };

  const handleCSVUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith(".csv")) {
      setErrors((prev) => ({ ...prev, csvFile: "Please upload a CSV file" }));
      setCsvValidationErrors([]);
      setCsvContactCount(0);
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target.result;
        const lines = text.split("\n").filter((line) => line.trim());
        
        if (lines.length === 0) {
          setErrors((prev) => ({ ...prev, csvFile: "CSV file is empty" }));
          setCsvContactCount(0);
          return;
        }

        const headers = parseCSVLine(lines[0]).map((h) => h.replace(/^"|"$/g, '').trim());

        // Phone-column auto-detect is best-effort. The user picks the
        // actual phone column on the mapping panel below, so we no
        // longer hard-fail here when the literal "phone_number" header
        // is missing.
        const PHONE_ALIASES = [
          "phone_number", "phone", "phone number", "mobile", "mobile_number",
          "contact", "contact_number", "whatsapp", "whatsapp_number", "wa",
          "wa_number", "number",
        ];
        const phoneColumnKey = headers.find((h) =>
          PHONE_ALIASES.includes(String(h).toLowerCase().trim())
        );

        const allDataRows = lines.slice(1).map((line) => {
          const values = parseCSVLine(line).map((v) => v.replace(/^"|"$/g, '').trim());
          const row = {};
          headers.forEach((header, index) => {
            row[header] = values[index] || "";
          });
          return row;
        }).filter(row => Object.values(row).some(val => val && val.trim()));

        // Best-effort row count — uses the auto-detected phone column.
        // If we couldn't auto-detect we count all non-empty rows so
        // the user still sees their data is parsing.
        const validContacts = phoneColumnKey
          ? allDataRows.filter((row) => row[phoneColumnKey] && row[phoneColumnKey].toString().trim())
          : allDataRows;

        const totalContacts = validContacts.length;
        
        // Preview data (first 5 rows)
        const previewData = validContacts.slice(0, 5);

        setCampaignData((prev) => ({
          ...prev,
          csvFile: file,
          csvData: previewData,
        }));
        // Headers feed the mapping panel so users can pick which CSV
        // column drives each template variable.
        setCsvColumns(headers);
        // Drop any prior mapping (it likely points at columns from a
        // previous file). The panel will auto-suggest fresh matches.
        setVariableMappings({});
        setCsvContactCount(totalContacts);
        setErrors((prev) => ({ ...prev, csvFile: "" }));
        setCsvValidationErrors([]);
      } catch (error) {
        console.error("Error parsing CSV:", error);
        setErrors((prev) => ({ 
          ...prev, 
          csvFile: "Error parsing CSV file. Please check the format." 
        }));
        setCsvContactCount(0);
      }
    };
    reader.readAsText(file);
  };

  const extractRequiredParameters = (template) => {
    if (!template) {
      return [];
    }

    const parameters = [];
    const parameterFormat = getParameterFormat(template);

    let templateJson = template.template_json || {};
    if (typeof templateJson === 'string') {
      try {
        templateJson = JSON.parse(templateJson);
      } catch (e) {
        templateJson = {};
      }
    }

    const components = templateJson.components || [];

    const headerComponent = components.find(c => c.type === 'HEADER');
    if (headerComponent) {
      const headerFormat = headerComponent.format?.toUpperCase() || template.headerFormat?.toUpperCase() || 'NONE';
      const headerText = headerComponent.text || template.headerText || '';
      
      if (headerFormat === 'TEXT') {
        if (parameterFormat === "named" && headerComponent.example?.header_text_named_params) {
          headerComponent.example.header_text_named_params.forEach((param) => {
            const paramName = param?.param_name;
            if (!paramName) return;
            parameters.push({
              key: paramName,
              component: 'HEADER',
              paramName,
              placeholder: `{{${paramName}}}`,
              label: `Header Variable: ${paramName}`,
              description: `Value for {{${paramName}}} in header`,
            });
          });
        } else {
          const headerVarMatches = headerText.match(/\{\{(\d+)\}\}/g) || [];
          headerVarMatches.forEach((match) => {
            const varNum = match.replace(/\{\{|\}\}/g, '');
            parameters.push({
              key: `header_${varNum}`,
              component: 'HEADER',
              varNum,
              placeholder: match,
              label: `Header Variable ${varNum}`,
              description: `Value for ${match} in header`,
            });
          });
        }
      }
    } else if (template.headerFormat || template.headerType) {
      const headerFormat = (template.headerFormat || template.headerType).toUpperCase();
      const headerText = template.headerText || '';
      
      if (headerFormat === 'TEXT') {
        const headerVarMatches = headerText.match(/\{\{(\d+)\}\}/g) || [];
        headerVarMatches.forEach((match) => {
          const varNum = match.replace(/\{\{|\}\}/g, '');
          parameters.push({
            key: `header_${varNum}`,
            component: 'HEADER',
            varNum,
            placeholder: match,
            label: `Header Variable ${varNum}`,
            description: `Value for ${match} in header`,
          });
        });
      }
    }

    const bodyComponent = components.find(c => c.type === 'BODY');
    const bodyText = bodyComponent?.text || template.body || '';
    if (parameterFormat === "named" && bodyComponent?.example?.body_text_named_params) {
      bodyComponent.example.body_text_named_params.forEach((param) => {
        const paramName = param?.param_name;
        if (!paramName) return;
        parameters.push({
          key: paramName,
          component: 'BODY',
          paramName,
          placeholder: `{{${paramName}}}`,
          label: `Body Variable: ${paramName}`,
          description: `Value for {{${paramName}}} in body text`,
        });
      });
    } else {
      const bodyVarMatches = bodyText.match(/\{\{(\d+)\}\}/g) || [];
      bodyVarMatches.forEach((match) => {
        const varNum = match.replace(/\{\{|\}\}/g, '');
        parameters.push({
          key: `body_${varNum}`,
          component: 'BODY',
          varNum,
          placeholder: match,
          label: `Body Variable ${varNum}`,
          description: `Value for ${match} in body text`,
        });
      });
    }

    const buttonsComponent = components.find(c => c.type === 'BUTTONS');
    const buttons = buttonsComponent?.buttons || template.buttons || [];
    buttons.forEach((btn, index) => {
      if (btn.type === 'URL' && btn.url && btn.url.includes('{{')) {
        parameters.push({
          key: `button_text_${index}`,
          component: 'BUTTON',
          index,
          placeholder: 'Button URL variable',
          label: `Button ${index + 1} Text`,
          description: `Text for URL button ${index + 1}`,
        });
      }
    });

    return parameters;
  };

  // The template the user is currently previewing — this is the one the
  // test message is sent for (so it matches what's on screen when multiple
  // templates are selected).
  const getActiveTestTemplate = () => {
    const tid = selectedTemplates[activePreviewIndex];
    return (
      templates.find((t) => String(t.id).trim() === String(tid).trim()) ||
      originalTemplate ||
      null
    );
  };

  // Pull Meta's example/sample value for a given extracted parameter so the
  // test inputs start pre-filled and a user can test in one click.
  const getSampleForParam = (template, param) => {
    if (!template || !param) return "";
    let templateJson = template.template_json || {};
    if (typeof templateJson === "string") {
      try {
        templateJson = JSON.parse(templateJson);
      } catch (e) {
        templateJson = {};
      }
    }
    const components = templateJson.components || [];

    if (param.component === "BODY") {
      const body = components.find((c) => c.type === "BODY");
      if (param.paramName) {
        const np = body?.example?.body_text_named_params || [];
        const found = np.find((p) => p?.param_name === param.paramName);
        return found?.example || "";
      }
      const idx = parseInt(param.varNum, 10) - 1;
      return body?.example?.body_text?.[0]?.[idx] || "";
    }

    if (param.component === "HEADER") {
      const header = components.find((c) => c.type === "HEADER");
      if (param.paramName) {
        const np = header?.example?.header_text_named_params || [];
        const found = np.find((p) => p?.param_name === param.paramName);
        return found?.example || "";
      }
      return header?.example?.header_text?.[0] || "";
    }

    return "";
  };

  // When the test panel opens (or the previewed template changes) seed the
  // test inputs with the active template's required variables, pre-filling
  // sample values. Existing user-entered values are preserved.
  useEffect(() => {
    if (!showTestSection) return;
    const activeTemplate = getActiveTestTemplate();
    if (!activeTemplate) {
      setTestValues({});
      return;
    }
    const params = extractRequiredParameters(activeTemplate);
    setTestValues((prev) => {
      const next = {};
      params.forEach((param) => {
        next[param.key] =
          prev[param.key] !== undefined && prev[param.key] !== ""
            ? prev[param.key]
            : getSampleForParam(activeTemplate, param);
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTestSection, activePreviewIndex, selectedTemplates]);

  // Union of required variables across ALL selected templates (deduped by
  // key). The mapping panels use this instead of just the first template so
  // that, in a multi-template campaign, every variable any template needs is
  // mappable. Falls back to originalTemplate when the lookup list isn't ready.
  const getSelectedTemplatesParams = () => {
    const seen = new Set();
    const union = [];
    const ids = selectedTemplates.length
      ? selectedTemplates
      : originalTemplate
      ? [originalTemplate.id]
      : [];
    ids.forEach((tid) => {
      const tpl =
        templates.find((t) => String(t.id).trim() === String(tid).trim()) ||
        (originalTemplate && String(originalTemplate.id).trim() === String(tid).trim()
          ? originalTemplate
          : null);
      if (!tpl) return;
      extractRequiredParameters(tpl).forEach((p) => {
        if (!seen.has(p.key)) {
          seen.add(p.key);
          union.push(p);
        }
      });
    });
    return union;
  };

  const generateCSVHeaders = (template) => sharedGenerateCSVHeaders(template);

  const downloadSampleCSV = () => {
    if (!selectedTemplateForPreview || !originalTemplate) {
      alert("Please select at least one template first to download sample CSV");
      return;
    }

    const headers = generateCSVHeaders(originalTemplate);
    
    const sampleRows = [];
    for (let i = 0; i < 2; i++) {
      const row = headers.map((header) => sampleValueForHeader(header, i));
      sampleRows.push(row.join(','));
    }

    const csvContent = [headers.join(','), ...sampleRows].join('\n');
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `campaign_sample_${originalTemplate.name || 'template'}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleHeaderMediaChange = (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = ""; // allow re-selecting same file later
    if (!file) return;

    if (!headerMediaLimits) {
      setHeaderMediaError("This template's header does not accept media uploads.");
      return;
    }

    const result = validateHeaderMediaFile(file, headerMediaFormat);
    if (!result.valid) {
      setHeaderMediaError(result.error);
      return;
    }

    if (headerMediaPreviewUrl) URL.revokeObjectURL(headerMediaPreviewUrl);
    const previewUrl = URL.createObjectURL(file);
    setHeaderMediaFile(file);
    setHeaderMediaPreviewUrl(previewUrl);
    setHeaderMediaError("");
  };

  const handleRemoveHeaderMedia = () => {
    if (headerMediaPreviewUrl) URL.revokeObjectURL(headerMediaPreviewUrl);
    setHeaderMediaFile(null);
    setHeaderMediaPreviewUrl("");
    setHeaderMediaError("");
    if (headerMediaInputRef.current) {
      headerMediaInputRef.current.value = "";
    }
  };

  const handleSubmit = async () => {
    const newErrors = {};
    if (!campaignData.name.trim()) {
      newErrors.name = "Campaign name is required";
    }
    if (!campaignData.templateIds || campaignData.templateIds.length === 0) {
      newErrors.templateIds = "Please select at least one template";
    }
    if (templateValidationError) {
      newErrors.templateIds = templateValidationError;
    }
    if (!campaignData.whatsapp_account_id) {
      newErrors.whatsapp_account_id = "Please select a phone number";
    }
    if (useSegment) {
      if (!campaignData.segment_id) {
        newErrors.segment_id = "Please select a contact list";
      }
    } else {
      if (!campaignData.csvFile) {
        newErrors.csvFile = "Please upload a CSV file";
      } else {
        // Phone is the only truly mandatory mapping in CSV mode. The
        // server validates this too, but blocking client-side gives a
        // friendlier "fix it in front of you" UX.
        const phoneRule = variableMappings?.phone;
        const phoneOk =
          phoneRule &&
          ((phoneRule.source === "csv_column" && phoneRule.csv_column) ||
            (phoneRule.source === "literal" && phoneRule.value));
        if (!phoneOk) {
          newErrors.csvFile = "Choose which CSV column contains the phone number.";
          setVariableMappingErrors((prev) => ({
            ...prev,
            phone: "Choose which CSV column contains the phone number.",
          }));
        }
      }
    }
    if (campaignData.sendOption === "schedule") {
      if (!campaignData.scheduledDate || !campaignData.scheduledDate.trim()) {
        newErrors.scheduledDate = "Please select a scheduled date";
      }
      
      if (!campaignData.scheduledTime || !campaignData.scheduledTime.trim()) {
        newErrors.scheduledTime = "Please select a scheduled time";
      }
      
      if (campaignData.scheduledDate && campaignData.scheduledTime) {
        const timeWithSeconds = campaignData.scheduledTime.length === 5 
          ? `${campaignData.scheduledTime}:00` 
          : campaignData.scheduledTime;
        const timestamp = `${campaignData.scheduledDate} ${timeWithSeconds}`;
        const timestampRegex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;
        
        if (!timestampRegex.test(timestamp)) {
          newErrors.scheduledTimestamp = "Invalid date or time format";
        } else {
          // Validate timezone is selected
          if (!campaignData.timezone || !campaignData.timezone.trim()) {
            newErrors.timezone = "Please select a timezone";
          } else {
            // Parse scheduled date/time in the selected timezone
            const scheduledMoment = moment.tz(
              `${campaignData.scheduledDate} ${campaignData.scheduledTime}`,
              "YYYY-MM-DD HH:mm",
              campaignData.timezone
            );
            
            if (!scheduledMoment.isValid()) {
              newErrors.scheduledTimestamp = "Invalid date or time values";
            } else {
              // Get current time in the selected timezone
              const nowInTimezone = moment.tz(campaignData.timezone);
              
              // Compare in the same timezone
              if (scheduledMoment.isSameOrBefore(nowInTimezone)) {
                newErrors.scheduledTimestamp = "Scheduled time must be in the future";
              }
            }
          }
        }
      } else if (!campaignData.scheduledTimestamp || !campaignData.scheduledTimestamp.trim()) {
        newErrors.scheduledTimestamp = "Please select both date and time";
      }
      
      if (!campaignData.timezone || !campaignData.timezone.trim()) {
        newErrors.timezone = "Please select a timezone";
      }
    }
    setErrors(newErrors);
    setCsvValidationErrors([]);
    setVariableMappingErrors({});
    
    if (Object.keys(newErrors).length === 0) {
      setIsSubmitting(true);
      try {
        const filteredDefaults = {};
        Object.keys(defaultValues).forEach(key => {
          // Allow spaces, including just " " as valid value
          // Only exclude null, undefined, or empty string (allow spaces like " ", "  ", etc.)
          // Allow whitespace-only values (e.g. " ") as valid template content.
          if (defaultValues[key] !== null && defaultValues[key] !== undefined && defaultValues[key] !== "") {
            filteredDefaults[key] = defaultValues[key];
          }
        });

        // The "Use Contact List" toggle now feeds the centralized
        // contact-lists module (not the legacy segments table). We
        // therefore submit `contact_list_id` + `use_contact_list` and
        // leave `segment_id`/`use_segment` null. variable_mappings is
        // resolved at create time on the server, so the broadcaster
        // never re-derives values. The same mapping object is sent in
        // both CSV mode (csv_column source) and Contact-list mode
        // (field/custom_field/computed sources).
        const hasMappings = Object.keys(variableMappings || {}).length > 0;
        const submitData = {
          campaign_name: campaignData.name,
          description: campaignData.description || "",
          template_ids: campaignData.templateIds,
          whatsapp_account_id: campaignData.whatsapp_account_id,
          segment_id: null,
          use_segment: false,
          contact_list_id: useSegment ? campaignData.segment_id : null,
          use_contact_list: !!useSegment,
          variable_mappings: hasMappings ? variableMappings : null,
          csvFile: !useSegment ? campaignData.csvFile : null,
          headerMediaFile: headerMediaLimits ? headerMediaFile : null,
          defaultValues: Object.keys(filteredDefaults).length > 0 ? filteredDefaults : null,
          variables: {},
          sendOption: campaignData.sendOption,
          scheduledTimestamp: campaignData.sendOption === "schedule"
            ? campaignData.scheduledTimestamp.trim()
            : null,
          timezone: campaignData.sendOption === "schedule"
            ? campaignData.timezone
            : null,
        };
        
        await onSubmit(submitData);
      } catch (error) {
        console.error("Error creating campaign:", error);
        
        const errorResponse = error.response?.data;
        if (errorResponse?.validation?.errors) {
          const validationErrors = errorResponse.validation.errors;
          const summary = errorResponse.validation.summary;
          const variableErrors = errorResponse.validation.variableErrors || {};
          setVariableMappingErrors(variableErrors);

          let summaryMessage = errorResponse.error || "CSV validation failed";
          if (summary && typeof summary === 'object') {
            const { totalRows, validRows, invalidRows } = summary;
            summaryMessage = `CSV validation failed: ${invalidRows} of ${totalRows} rows have errors`;
          } else if (typeof summary === 'string') {
            summaryMessage = summary;
          }
          
          setErrors((prev) => ({
            ...prev,
            csvFile: summaryMessage,
          }));
          
          let errorsArray = [];
          if (Array.isArray(validationErrors)) {
            errorsArray = validationErrors.map(err => {
              if (typeof err === 'string') {
                return err;
              } else if (typeof err === 'object') {
                return JSON.stringify(err);
              }
              return String(err);
            });
          } else if (validationErrors) {
            errorsArray = [typeof validationErrors === 'string' ? validationErrors : JSON.stringify(validationErrors)];
          }
          
          setCsvValidationErrors(errorsArray);
        } else {
          const errorMessage = errorResponse?.error || error.message || "Failed to create campaign";
          console.error("Campaign creation error:", errorMessage);
        }
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  // Carousel Navigation
  const navigatePreview = (direction) => {
    setActivePreviewIndex((prev) => {
      if (direction === 'next') {
        return prev < selectedTemplates.length - 1 ? prev + 1 : 0;
      } else {
        return prev > 0 ? prev - 1 : selectedTemplates.length - 1;
      }
    });
  };

  // Handle sending test message
  const handleSendTest = async () => {
    const localDigits = testPhoneNumber.replace(/\D/g, "");
    if (!localDigits) {
      setTestResult({ success: false, message: "Please enter a phone number" });
      return;
    }

    const activeTemplate = getActiveTestTemplate();
    if (!selectedTemplates.length || !activeTemplate) {
      setTestResult({ success: false, message: "Please select a template first" });
      return;
    }

    const phoneIdToUse = campaignData.whatsapp_account_id || selectedPhoneAccount?.phoneId;
    if (!phoneIdToUse) {
      setTestResult({ success: false, message: "No WhatsApp account selected" });
      return;
    }

    setIsSendingTest(true);
    setTestResult(null);

    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");

      // Compose the recipient from the selected country code + local number.
      // Meta expects digits only, no leading "+".
      const dialDigits = testCountryCode.replace(/\D/g, "");
      const cleanPhone = `${dialDigits}${localDigits}`;

      // Build userVars from the test-only sample values (active template).
      // The wabaserver TemplateSender always expects NUMBERED keys
      // (body_1, header_1, …) — even for named templates, which it maps back to
      // parameter_name from the template definition. Our testValues are keyed by
      // param.key, which for named templates is the param NAME (e.g.
      // "customer_name"), not body_1. So we translate named params to their
      // positional numbered key here; numbered params already use the right key.
      const userVars = {};
      let bodyPos = 0;
      let headerPos = 0;
      extractRequiredParameters(activeTemplate).forEach((param) => {
        const value = testValues[param.key];

        let targetKey = param.key;
        if (param.component === "BODY") {
          bodyPos += 1;
          if (param.paramName) targetKey = `body_${bodyPos}`;
        } else if (param.component === "HEADER") {
          headerPos += 1;
          if (param.paramName) targetKey = `header_${headerPos}`;
        }

        if (value !== null && value !== undefined && value !== "") {
          userVars[targetKey] = value;
        }
      });

      const testData = {
        userId: user.id,
        phoneId: phoneIdToUse,
        template: activeTemplate,
        phone_number: cleanPhone,
        userVars: userVars,
      };

      const response = await sendTestTemplate(testData);

      if (response.data.success) {
        setTestResult({
          success: true,
          message: `Test message sent successfully to ${cleanPhone}!`,
          messageId: response.data.messageId,
        });
      } else {
        setTestResult({
          success: false,
          message: response.data.message || "Failed to send test message",
        });
      }
    } catch (error) {
      console.error("Error sending test message:", error);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          "Failed to send test message";
      setTestResult({ success: false, message: errorMessage });
    } finally {
      setIsSendingTest(false);
    }
  };

  const phoneOptions = useMemo(
    () =>
      phoneAccounts.map((account) => ({
        value: account.phoneId,
        label: account.phoneName
          ? `${account.phoneNumber} · ${account.phoneName}`
          : account.phoneNumber,
        searchText: `${account.phoneNumber} ${account.phoneName || ""}`.toLowerCase(),
      })),
    [phoneAccounts]
  );

  const selectedPhoneOption = useMemo(
    () => phoneOptions.find((o) => o.value === campaignData.whatsapp_account_id) || null,
    [phoneOptions, campaignData.whatsapp_account_id]
  );

  const templateOptions = useMemo(
    () =>
      templates.map((template) => ({
        value: String(template.id).trim(),
        label: template.name,
        template,
        searchText: `${template.name} ${template.category || ""} ${template.language || ""}`.toLowerCase(),
      })),
    [templates]
  );

  const selectedTemplateOptions = useMemo(
    () =>
      templateOptions.filter((o) =>
        selectedTemplates.some((id) => String(id).trim() === o.value)
      ),
    [templateOptions, selectedTemplates]
  );

  const contactListOptions = useMemo(
    () =>
      contactLists.map((list) => {
        const count = list.member_count ?? list.contact_count ?? 0;
        return {
          value: String(list.id),
          label: `${list.name} · ${count.toLocaleString()} contacts`,
          searchText: (list.name || "").toLowerCase(),
        };
      }),
    [contactLists]
  );

  const selectedContactListOption = useMemo(
    () =>
      contactListOptions.find((o) => o.value === String(campaignData.segment_id)) || null,
    [contactListOptions, campaignData.segment_id]
  );

  const timezoneOptions = useMemo(
    () =>
      [
        "America/New_York",
        "America/Chicago",
        "America/Denver",
        "America/Los_Angeles",
        "America/Toronto",
        "Europe/London",
        "Europe/Paris",
        "Europe/Berlin",
        "Asia/Dubai",
        "Asia/Kolkata",
        "Asia/Singapore",
        "Asia/Tokyo",
        "Asia/Shanghai",
        "Australia/Sydney",
      ].map((tz) => ({
        value: tz,
        label: tz === "Asia/Kolkata" ? "Asia/Kolkata (India IST)" : tz.replace(/_/g, " "),
        searchText: tz.toLowerCase(),
      })),
    []
  );

  const selectedTimezoneOption = useMemo(
    () => timezoneOptions.find((o) => o.value === campaignData.timezone) || null,
    [timezoneOptions, campaignData.timezone]
  );

  const formatTemplateOptionLabel = (option, { context }) => {
    if (context === "value" || !option.template) {
      return option.label;
    }
    const t = option.template;
    const paramFormat = getParameterFormat(t);
    const qualityConfig = getQualityBadge(t.quality_score);
    return (
      <div className="py-0.5">
        <p className="font-semibold text-gray-900 truncate">{t.name}</p>
        <div className="flex flex-wrap items-center gap-1.5 mt-1">
          {t.category && (
            <span className="text-[11px] text-gray-500">{t.category}</span>
          )}
          <span
            className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${
              paramFormat === "named"
                ? "bg-purple-50 text-purple-700 border border-purple-200"
                : "bg-slate-100 text-slate-600 border border-slate-200"
            }`}
          >
            {paramFormat === "named" ? "Named" : "Numbered"}
          </span>
          {t.language && (
            <span className="text-[10px] text-gray-400 uppercase">{t.language}</span>
          )}
          <span
            className={`text-[10px] px-1.5 py-0.5 rounded font-semibold border ${qualityConfig.class}`}
          >
            {qualityConfig.label}
          </span>
        </div>
      </div>
    );
  };

  const templateSelectionMeta = useMemo(() => {
    if (!selectedTemplates.length) return null;
    const objs = selectedTemplates
      .map((id) => templates.find((t) => String(t.id).trim() === String(id).trim()))
      .filter(Boolean);
    if (!objs.length) return null;
    const formats = objs.map((t) => getParameterFormat(t));
    const allNamed = formats.every((f) => f === "named");
    const allNumbered = formats.every((f) => f === "numbered");
    return {
      count: objs.length,
      mixed: !allNamed && !allNumbered,
      formatLabel: allNamed ? "Named parameters" : allNumbered ? "Numbered parameters" : "Mixed parameter types",
    };
  }, [selectedTemplates, templates]);

  return (
    <div className="min-h-screen bg-[#f8faf9]">
      <div className="border-b border-gray-200 bg-white sticky top-0 z-20 shadow-sm">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={onCancel}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-all border border-gray-200 shrink-0"
              title="Go back"
            >
              <ArrowLeft size={20} />
            </button>
            <div className="min-w-0">
              <h1 className="text-xl font-bold text-gray-900 truncate">Create Campaign</h1>
              <p className="text-sm text-gray-500">Choose templates, audience, and send options</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={onCancel}
              className="px-4 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className={`px-5 py-2.5 text-sm font-bold text-gray-900 rounded-xl transition-all flex items-center gap-2 border border-emerald-300 bg-[#eefa94] hover:bg-[#e5f580] ${
                isSubmitting ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {isSubmitting && <Loader className="w-4 h-4 animate-spin" />}
              {isSubmitting
                ? "Creating..."
                : campaignData.sendOption === "now"
                  ? "Create & Send"
                  : "Create & Schedule"}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 py-6 lg:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(360px,420px)] gap-6 lg:gap-8 items-start">
          {/* Left: Form */}
          <div className="space-y-6 min-w-0">
            {/* Campaign Info */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 sm:p-6">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-9 h-9 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center">
                  <FileText size={18} className="text-emerald-600" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-gray-900">Campaign details</h2>
                  <p className="text-xs text-gray-500">Name, description, and sending number</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-1">
                  <label className="block text-xs font-bold text-gray-700 mb-2">
                    Campaign Name *
                  </label>
                  <input
                    type="text"
                    value={campaignData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    placeholder="e.g., Welcome Campaign"
                    className={`w-full px-4 py-2.5 text-sm border ${
                      errors.name ? "border-red-300" : "border-gray-300"
                    } rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-400`}
                  />
                  {errors.name && (
                    <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                      <AlertCircle size={12} />
                      {errors.name}
                    </p>
                  )}
                </div>
                <div className="md:col-span-1">
                  <label className="block text-xs font-bold text-gray-700 mb-2">
                    Send From Phone Number *
                  </label>
                  {loadingPhoneAccounts ? (
                    <div className="flex items-center gap-2 px-4 py-3 text-sm text-gray-500 border border-gray-200 rounded-xl bg-gray-50">
                      <Loader className="w-4 h-4 animate-spin" />
                      Loading phone numbers…
                    </div>
                  ) : (
                    <SearchableSelect
                      options={phoneOptions}
                      value={selectedPhoneOption}
                      onChange={(opt) => {
                        const val = opt?.value || "";
                        if (String(val) !== String(campaignData.whatsapp_account_id)) {
                          lastLoadedPhoneIdRef.current = null;
                        }
                        handleInputChange("whatsapp_account_id", val);
                      }}
                      placeholder="Search phone numbers…"
                      isDisabled={phoneOptions.length === 0}
                      error={Boolean(errors.whatsapp_account_id)}
                      noOptionsMessage={() => "No phone numbers available"}
                    />
                  )}
                  {errors.whatsapp_account_id && (
                    <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                      <AlertCircle size={12} />
                      {errors.whatsapp_account_id}
                    </p>
                  )}
                  {phoneAccounts.length === 0 && !loadingPhoneAccounts && (
                    <p className="text-xs text-gray-500 mt-2 font-medium">
                      No phone numbers available. Please add a WhatsApp Business account first.
                    </p>
                  )}
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-bold text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={campaignData.description}
                    onChange={(e) =>
                      handleInputChange("description", e.target.value)
                    }
                    placeholder="Describe your campaign..."
                    rows={3}
                    className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-400 resize-none"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <div className="space-y-6 min-w-0">
            {/* Template Selection */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-5 sm:px-6 py-4 border-b border-gray-100 flex items-start justify-between gap-3 bg-gradient-to-r from-gray-50/60 to-white">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center shrink-0 mt-0.5">
                    <Sparkles size={18} className="text-emerald-600" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-base font-bold text-gray-900">Templates</h2>
                    <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                      Search and add templates. Review each selection in the preview panel on the right.
                    </p>
                  </div>
                </div>
                {selectedTemplates.length > 0 && (
                  <span className="text-xs font-semibold text-gray-600 bg-white border border-gray-200 px-2.5 py-1 rounded-lg shrink-0 tabular-nums">
                    {selectedTemplates.length} selected
                  </span>
                )}
              </div>

              <div className="p-5 sm:p-6">
              {!campaignData.whatsapp_account_id ? (
                <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-100 rounded-xl">
                  <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-900">
                    Select a phone number above to load approved templates for that account.
                  </p>
                </div>
              ) : loading ? (
                <div className="flex items-center justify-center gap-2 py-12 text-sm text-gray-500">
                  <Loader className="w-5 h-5 animate-spin text-emerald-600" />
                  Loading templates…
                </div>
              ) : templates.length === 0 ? (
                <div className="text-center py-10 px-4 border border-dashed border-gray-200 rounded-xl bg-gray-50/50">
                  <FileText className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm font-semibold text-gray-800">No approved templates</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Create and approve a template first, then return here to launch a campaign.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <SearchableSelect
                    isMulti
                    options={templateOptions}
                    value={selectedTemplateOptions}
                    onChange={(opts) =>
                      applyTemplateSelection((opts || []).map((o) => o.value))
                    }
                    placeholder="Type to search templates…"
                    formatOptionLabel={formatTemplateOptionLabel}
                    hideSelectedOptions
                    closeMenuOnSelect={false}
                    noOptionsMessage={() => "No templates match your search"}
                    error={Boolean(errors.templateIds || templateValidationError)}
                  />

                  {templateSelectionMeta && !templateValidationError && (
                    <div className="flex flex-wrap items-center gap-2 pt-1">
                      <span
                        className={`inline-flex items-center text-[11px] font-semibold px-2 py-1 rounded-md border ${
                          templateSelectionMeta.mixed
                            ? "bg-amber-50 text-amber-800 border-amber-200"
                            : "bg-gray-100 text-gray-600 border-gray-200"
                        }`}
                      >
                        {templateSelectionMeta.formatLabel}
                      </span>
                      {templateSelectionMeta.count > 1 && (
                        <span className="text-xs text-gray-500 inline-flex items-center gap-1">
                          <Sparkles size={12} className="text-emerald-500" />
                          Randomly assigned per contact at send time
                        </span>
                      )}
                    </div>
                  )}

                  {(errors.templateIds || templateValidationError) && (
                    <div className="flex items-start gap-2.5 px-3 py-2.5 rounded-xl bg-red-50 border border-red-100">
                      <AlertCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                      <p className="text-sm text-red-700 leading-snug">
                        {errors.templateIds || templateValidationError}
                      </p>
                    </div>
                  )}
                </div>
              )}
              </div>
            </div>

            {/* Header Media Override (Image / Video / Document) */}
            {headerMediaLimits && (() => {
              const Icon = headerMediaLimits.icon;
              return (
                <div className="bg-white rounded-2xl border border-gray-200 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon size={16} className="text-emerald-600" />
                    <h2 className="text-sm font-bold text-gray-900">
                      Header {headerMediaLimits.label} *
                    </h2>
                  </div>
                  <p className="text-xs text-gray-600 mb-3 font-medium">
                    Upload a single {headerMediaLimits.label.toLowerCase()} that will be used as the header for{" "}
                    <strong>every contact</strong> in this campaign — it overrides any per-contact value.
                  </p>

                  <div className="text-[11px] text-gray-500 mb-3 flex flex-wrap gap-x-3 gap-y-1 font-medium">
                    <span>
                      <strong>Allowed:</strong>{" "}
                      {headerMediaLimits.extensions.join(", ")}
                    </span>
                    <span>
                      <strong>Max size:</strong> {formatBytes(headerMediaLimits.maxBytes)} (Meta limit)
                    </span>
                  </div>

                  {!headerMediaFile ? (
                    <label className="block">
                      <div className="border-2 border-dashed border-gray-300 rounded-xl p-5 text-center hover:border-emerald-400 transition-colors cursor-pointer bg-gray-50">
                        <Upload className="mx-auto mb-2 text-gray-400" size={28} />
                        <p className="text-sm font-bold text-gray-700 mb-1">
                          Click to upload {headerMediaLimits.label.toLowerCase()}
                        </p>
                        <p className="text-xs text-gray-500 font-medium">
                          {headerMediaLimits.extensions.join(", ")} •{" "}
                          up to {formatBytes(headerMediaLimits.maxBytes)}
                        </p>
                      </div>
                      <input
                        ref={headerMediaInputRef}
                        type="file"
                        accept={headerMediaLimits.accept}
                        onChange={handleHeaderMediaChange}
                        className="hidden"
                      />
                    </label>
                  ) : (
                    <div className="border border-emerald-200 bg-emerald-50 rounded-xl p-3">
                      <div className="flex items-start gap-3">
                        {headerMediaFormat === "IMAGE" && headerMediaPreviewUrl ? (
                          <img
                            src={headerMediaPreviewUrl}
                            alt="Header preview"
                            className="w-16 h-16 rounded-lg object-cover border border-emerald-200 flex-shrink-0"
                          />
                        ) : (
                          <div className="w-16 h-16 rounded-lg bg-white border border-emerald-200 flex items-center justify-center flex-shrink-0">
                            <Icon size={28} className="text-emerald-600" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-gray-900 truncate">
                            {headerMediaFile.name}
                          </p>
                          <p className="text-xs text-gray-600 mt-0.5 font-medium">
                            {formatBytes(headerMediaFile.size)} •{" "}
                            {headerMediaFile.type || headerMediaLimits.label}
                          </p>
                          <p className="text-[11px] text-emerald-700 mt-1 flex items-center gap-1 font-medium">
                            <CheckCircle size={12} />
                            Will be used for all campaign contacts
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={handleRemoveHeaderMedia}
                          className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                          title="Remove file"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    </div>
                  )}

                  {headerMediaError && (
                    <div className="mt-3 flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl">
                      <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-red-700 font-medium">{headerMediaError}</p>
                    </div>
                  )}
                </div>
              );
            })()}

              </div>

              <div className="space-y-6 min-w-0">
            {/* Recipients Selection */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-5 sm:px-6 py-4 border-b border-gray-100 flex items-start justify-between gap-3 bg-gradient-to-r from-gray-50/60 to-white">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center shrink-0 mt-0.5">
                    <Users size={18} className="text-emerald-600" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-base font-bold text-gray-900">Audience</h2>
                    <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                      {useSegment
                        ? "Send to everyone in a saved contact list."
                        : "Upload a CSV with phone numbers and variable columns."}
                    </p>
                  </div>
                </div>
                {(useSegment && selectedSegmentContactCount > 0) ||
                (!useSegment && csvContactCount > 0) ? (
                  <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-100 px-2.5 py-1 rounded-lg shrink-0 tabular-nums">
                    {useSegment
                      ? `${selectedSegmentContactCount.toLocaleString()} contacts`
                      : `${csvContactCount.toLocaleString()} in CSV`}
                  </span>
                ) : null}
              </div>

              <div className="px-5 sm:px-6 pt-4 pb-5">
              <div className="flex gap-2 mb-4 p-1 bg-gray-100 rounded-xl">
                <button
                  type="button"
                  onClick={() => {
                    setUseSegment(false);
                    handleInputChange("use_segment", false);
                    setSelectedSegmentContactCount(0);
                    setVariableMappings({});
                  }}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-semibold transition-all ${
                    !useSegment
                      ? "bg-white text-gray-900 shadow-sm border border-gray-200"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  Upload CSV
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setUseSegment(true);
                    handleInputChange("use_segment", true);
                    setCsvContactCount(0);
                    setCampaignData((prev) => ({
                      ...prev,
                      csvFile: null,
                      csvData: [],
                    }));
                    setCsvColumns([]);
                    setVariableMappings({});
                  }}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-semibold transition-all ${
                    useSegment
                      ? "bg-white text-gray-900 shadow-sm border border-gray-200"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  Contact list
                </button>
              </div>

              {useSegment ? (
                <div className="space-y-3">
                  <SearchableSelect
                    options={contactListOptions}
                    value={selectedContactListOption}
                    onChange={(opt) => {
                      const listId = opt?.value || "";
                      handleInputChange("segment_id", listId);
                      setVariableMappings({});
                      if (listId) {
                        const chosen = contactLists.find((l) => String(l.id) === String(listId));
                        setSelectedSegmentContactCount(
                          chosen?.member_count ?? chosen?.contact_count ?? 0
                        );
                      } else {
                        setSelectedSegmentContactCount(0);
                      }
                    }}
                    placeholder="Search contact lists…"
                    isDisabled={contactListOptions.length === 0}
                    error={Boolean(errors.segment_id)}
                    noOptionsMessage={() => "No contact lists — create one in Contacts"}
                  />
                  {contactLists.length === 0 && (
                    <div className="flex items-start gap-2.5 px-3 py-2.5 rounded-xl bg-gray-50 border border-gray-100 text-xs text-gray-600">
                      <Info className="w-4 h-4 shrink-0 mt-0.5 text-gray-400" />
                      <span>
                        No lists for this number yet. Create one on the Contacts page, or upload a
                        CSV instead.
                      </span>
                    </div>
                  )}
                  {errors.segment_id && (
                    <div className="flex items-start gap-2.5 px-3 py-2.5 rounded-xl bg-red-50 border border-red-100">
                      <AlertCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                      <p className="text-sm text-red-700">{errors.segment_id}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <label className="block group">
                    <div
                      className={`flex items-center gap-3 p-3 rounded-xl border transition-colors cursor-pointer ${
                        errors.csvFile
                          ? "border-red-200 bg-red-50/30"
                          : campaignData.csvFile
                            ? "border-emerald-200 bg-emerald-50/40"
                            : "border-gray-200 bg-gray-50/50 hover:border-emerald-300 hover:bg-emerald-50/20"
                      }`}
                    >
                      <div
                        className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                          campaignData.csvFile ? "bg-emerald-100" : "bg-white border border-gray-200"
                        }`}
                      >
                        {campaignData.csvFile ? (
                          <FileText size={20} className="text-emerald-600" />
                        ) : (
                          <Upload size={20} className="text-gray-400 group-hover:text-emerald-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {campaignData.csvFile
                            ? campaignData.csvFile.name
                            : "Choose a CSV file"}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {campaignData.csvFile && csvContactCount > 0
                            ? `${csvContactCount.toLocaleString()} contact${csvContactCount !== 1 ? "s" : ""} detected`
                            : campaignData.csvFile
                              ? "Processing file…"
                              : "Include phone numbers and any template variable columns"}
                        </p>
                      </div>
                      <span
                        className="shrink-0 px-3 py-1.5 text-xs font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg group-hover:border-emerald-300 transition-colors"
                      >
                        Browse
                      </span>
                    </div>
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleCSVUpload}
                      className="hidden"
                    />
                  </label>

                  <div className="flex items-center justify-between gap-3 pt-1">
                    <button
                      type="button"
                      onClick={downloadSampleCSV}
                      disabled={!selectedTemplateForPreview || !originalTemplate}
                      className={`inline-flex items-center gap-1.5 text-xs font-semibold transition-colors ${
                        selectedTemplateForPreview && originalTemplate
                          ? "text-emerald-700 hover:text-emerald-800"
                          : "text-gray-400 cursor-not-allowed"
                      }`}
                      title={
                        !selectedTemplateForPreview
                          ? "Select a template first"
                          : "Download sample CSV with required columns"
                      }
                    >
                      <Download size={14} />
                      Download sample CSV
                    </button>
                    {!selectedTemplateForPreview && (
                      <span className="text-[11px] text-gray-400">Select templates first</span>
                    )}
                  </div>

                  {errors.csvFile && (
                    <div className="flex items-start gap-2.5 px-3 py-2.5 rounded-xl bg-red-50 border border-red-100">
                      <AlertCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-red-700 font-medium">{errors.csvFile}</p>
                        {Array.isArray(csvValidationErrors) && csvValidationErrors.length > 0 && (
                          <details className="mt-2">
                            <summary className="text-xs text-red-600 cursor-pointer hover:text-red-800 font-semibold">
                              View details ({csvValidationErrors.length})
                            </summary>
                            <ul className="mt-2 ml-3 list-disc space-y-1 max-h-40 overflow-y-auto text-xs text-red-700">
                              {csvValidationErrors.slice(0, 20).map((error, idx) => (
                                <li key={idx}>
                                  {typeof error === "string"
                                    ? error
                                    : typeof error === "object"
                                      ? JSON.stringify(error)
                                      : String(error)}
                                </li>
                              ))}
                              {csvValidationErrors.length > 20 && (
                                <li className="italic">
                                  … and {csvValidationErrors.length - 20} more
                                </li>
                              )}
                            </ul>
                          </details>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              </div>
            </div>

              </div>
            </div>

            {/* Variable mapping — full width so the table uses the whole form area */}
            {!useSegment && (
              <VariableMappingPanel
                mode="csv"
                templateParams={getSelectedTemplatesParams()}
                csvColumns={csvColumns}
                value={variableMappings}
                onChange={(next) => {
                  setVariableMappings(next);
                  setVariableMappingErrors({});
                }}
                inlineErrors={variableMappingErrors}
              />
            )}
            {useSegment &&
              campaignData.segment_id &&
              selectedTemplates.length > 0 && (
                <VariableMappingPanel
                  mode="contact_list"
                  templateParams={getSelectedTemplatesParams()}
                  listId={parseInt(campaignData.segment_id, 10)}
                  phoneId={campaignData.whatsapp_account_id || selectedPhoneId}
                  value={variableMappings}
                  onChange={(next) => {
                    setVariableMappings(next);
                    setVariableMappingErrors({});
                  }}
                  inlineErrors={variableMappingErrors}
                />
              )}

            {!useSegment && campaignData.csvData.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/80">
                  <h3 className="text-sm font-bold text-gray-900">CSV preview</h3>
                  <p className="text-xs text-gray-500">First 5 rows from your upload</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-xs">
                    <thead className="bg-gray-50 border-b border-gray-100">
                      <tr>
                        {Object.keys(campaignData.csvData[0] || {}).map((key) => (
                          <th
                            key={key}
                            className="px-4 py-2.5 text-left font-semibold text-gray-600 whitespace-nowrap"
                          >
                            {key}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {campaignData.csvData.map((row, index) => (
                        <tr key={index} className="hover:bg-gray-50/50">
                          {Object.values(row).map((cell, i) => (
                            <td key={i} className="px-4 py-2 text-gray-800 whitespace-nowrap max-w-[200px] truncate">
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Right: When to send + Preview */}
          <div className="lg:sticky lg:top-[88px] lg:self-start w-full min-w-0 space-y-4">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-9 h-9 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center">
                  <Calendar size={18} className="text-emerald-600" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-gray-900">When to send</h2>
                  <p className="text-xs text-gray-500">Send immediately or schedule for later</p>
                </div>
              </div>
              <div className="flex gap-2 mb-4 p-1 bg-gray-100 rounded-xl">
                <button
                  type="button"
                  onClick={() => {
                    handleInputChange("sendOption", "now");
                    handleInputChange("scheduledDate", "");
                    handleInputChange("scheduledTime", "");
                    handleInputChange("scheduledTimestamp", "");
                  }}
                  className={`flex-1 py-2.5 px-3 rounded-lg text-sm font-semibold transition-all ${
                    campaignData.sendOption === "now"
                      ? "bg-white text-gray-900 shadow-sm border border-gray-200"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  Send now
                </button>
                <button
                  type="button"
                  onClick={() => handleInputChange("sendOption", "schedule")}
                  className={`flex-1 py-2.5 px-3 rounded-lg text-sm font-semibold transition-all ${
                    campaignData.sendOption === "schedule"
                      ? "bg-white text-gray-900 shadow-sm border border-gray-200"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  Schedule
                </button>
              </div>
              {campaignData.sendOption === "schedule" && (
                <div className="space-y-3 pt-3 border-t border-gray-100">
                  <div className="grid grid-cols-1 gap-3">
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-2">
                        Scheduled date *
                      </label>
                      <input
                        type="date"
                        value={campaignData.scheduledDate}
                        onChange={(e) => handleInputChange("scheduledDate", e.target.value)}
                        min={new Date().toISOString().split("T")[0]}
                        className={`w-full px-4 py-2.5 text-sm border ${
                          errors.scheduledTimestamp || errors.scheduledDate
                            ? "border-red-300"
                            : "border-gray-300"
                        } rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-400 font-medium`}
                      />
                      {(errors.scheduledTimestamp || errors.scheduledDate) && (
                        <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                          <AlertCircle size={12} />
                          {errors.scheduledDate || errors.scheduledTimestamp}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-2">
                        Scheduled time *
                      </label>
                      <input
                        type="time"
                        value={campaignData.scheduledTime}
                        onChange={(e) => handleInputChange("scheduledTime", e.target.value)}
                        step="1"
                        className={`w-full px-4 py-2.5 text-sm border ${
                          errors.scheduledTimestamp || errors.scheduledTime
                            ? "border-red-300"
                            : "border-gray-300"
                        } rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-400 font-medium`}
                      />
                      {(errors.scheduledTimestamp || errors.scheduledTime) && (
                        <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                          <AlertCircle size={12} />
                          {errors.scheduledTime || errors.scheduledTimestamp}
                        </p>
                      )}
                    </div>
                  </div>
                  {campaignData.scheduledTimestamp && (
                    <div className="p-3 bg-gray-50 border border-gray-200 rounded-xl">
                      <p className="text-xs text-gray-600 mb-1 font-medium">Formatted timestamp</p>
                      <p className="text-sm font-mono font-bold text-gray-900 break-all">
                        {campaignData.scheduledTimestamp}
                      </p>
                    </div>
                  )}
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-2">
                      Timezone *
                    </label>
                    <SearchableSelect
                      options={timezoneOptions}
                      value={selectedTimezoneOption}
                      onChange={(opt) => handleInputChange("timezone", opt?.value || "")}
                      placeholder="Search timezone…"
                      error={Boolean(errors.timezone)}
                    />
                    {errors.timezone && (
                      <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                        <AlertCircle size={12} />
                        {errors.timezone}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden flex flex-col min-h-[min(560px,calc(100vh-280px))]">
              <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/80 shrink-0">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-9 h-9 rounded-xl bg-white border border-gray-200 flex items-center justify-center shrink-0">
                      <Eye size={18} className="text-emerald-600" />
                    </div>
                    <div className="min-w-0">
                      <h2 className="text-base font-bold text-gray-900">Preview & test</h2>
                      <p className="text-xs text-gray-500 truncate">
                        {selectedTemplates.length > 0
                          ? `${selectedTemplates.length} template${selectedTemplates.length > 1 ? "s" : ""} selected`
                          : "Select templates to preview messages"}
                      </p>
                    </div>
                  </div>
                  {selectedTemplates.length > 1 && (
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        type="button"
                        onClick={() => navigatePreview("prev")}
                        className="p-2 rounded-lg hover:bg-white border border-transparent hover:border-gray-200 transition-colors"
                        title="Previous template"
                      >
                        <ChevronLeft size={18} />
                      </button>
                      <span className="text-xs font-semibold text-gray-500 tabular-nums min-w-[3rem] text-center">
                        {activePreviewIndex + 1}/{selectedTemplates.length}
                      </span>
                      <button
                        type="button"
                        onClick={() => navigatePreview("next")}
                        className="p-2 rounded-lg hover:bg-white border border-transparent hover:border-gray-200 transition-colors"
                        title="Next template"
                      >
                        <ChevronRight size={18} />
                      </button>
                    </div>
                  )}
                </div>
                {selectedTemplates.length > 0 && (() => {
                  const template = templates.find(
                    (t) =>
                      String(t.id).trim() ===
                      String(selectedTemplates[activePreviewIndex]).trim()
                  );
                  return template ? (
                    <p className="text-sm font-semibold text-gray-800 mt-3 truncate">
                      {template.name}
                    </p>
                  ) : null;
                })()}
              </div>

              <div className="flex-1 flex flex-col min-h-0 p-5 gap-4">
              {selectedTemplates.length > 0 ? (
                <>
                  <div className="flex-1 min-h-[240px] flex flex-col">
                  {(() => {
                    const templateId = selectedTemplates[activePreviewIndex];
                    const template = templates.find(
                      (t) => String(t.id).trim() === String(templateId).trim()
                    );
                    if (!template) return null;

                    const selectedPhoneAccountForPreview = campaignData.whatsapp_account_id
                      ? phoneAccounts.find(
                          (p) => p.phoneId === campaignData.whatsapp_account_id
                        )
                      : selectedPhoneAccount;

                    const previewVars = showTestSection
                      ? { ...defaultValues, ...testValues }
                      : defaultValues;

                    const previewState = buildTemplatePreviewState(
                      template,
                      previewVars,
                      headerMediaPreviewUrl
                    );

                    if (!previewState) return null;

                    return (
                      <div className="space-y-3 flex-1 flex flex-col">
                        {selectedPhoneAccountForPreview && (
                          <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl shrink-0">
                            <div className="flex items-center gap-2">
                              <Phone className="text-emerald-600 shrink-0" size={16} />
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-bold text-emerald-900">
                                  Sending from
                                </p>
                                <p className="text-xs text-emerald-700 truncate">
                                  {selectedPhoneAccountForPreview.phoneName
                                    ? `${selectedPhoneAccountForPreview.phoneName} · ${selectedPhoneAccountForPreview.phoneNumber}`
                                    : selectedPhoneAccountForPreview.phoneNumber}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                        <div className="flex-1 min-h-0">
                          <TemplatePreview state={previewState} />
                        </div>
                      </div>
                    );
                  })()}
                  </div>

                  <div className="shrink-0 border-t border-gray-100 pt-4">
                    {!showTestSection ? (
                      <button
                        onClick={() => setShowTestSection(true)}
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-xl transition-all"
                      >
                        <Send size={16} />
                        Send test message
                      </button>
                    ) : (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-bold text-gray-900">
                            Test message
                          </span>
                          <button
                            onClick={() => {
                              setShowTestSection(false);
                              setTestPhoneNumber("");
                              setTestResult(null);
                              setTestValues({});
                            }}
                            className="p-1 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
                          >
                            <X size={16} />
                          </button>
                        </div>

                        {/* Per-variable sample values for the previewed template */}
                        {(() => {
                          const activeTemplate = getActiveTestTemplate();
                          const params = activeTemplate
                            ? extractRequiredParameters(activeTemplate)
                            : [];
                          if (params.length === 0) {
                            return (
                              <p className="text-xs text-gray-500 font-medium bg-gray-50 border border-gray-200 rounded-xl p-3">
                                This template has no variables. Just enter a number below and send the test.
                              </p>
                            );
                          }
                          return (
                            <div className="space-y-2">
                              <p className="text-xs font-bold text-gray-700">
                                Sample values ({params.length})
                              </p>
                              {params.map((param) => (
                                <div key={param.key}>
                                  <label className="block text-[11px] font-semibold text-gray-500 mb-1">
                                    {param.label}{" "}
                                    <span className="text-gray-400 font-normal">
                                      {param.placeholder}
                                    </span>
                                  </label>
                                  <input
                                    type="text"
                                    value={testValues[param.key] ?? ""}
                                    onChange={(e) =>
                                      setTestValues((prev) => ({
                                        ...prev,
                                        [param.key]: e.target.value,
                                      }))
                                    }
                                    placeholder={`Sample for ${param.placeholder}`}
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-400"
                                  />
                                </div>
                              ))}
                            </div>
                          );
                        })()}

                        <div>
                          <label className="block text-xs font-bold text-gray-600 mb-2">
                            Recipient Phone Number
                          </label>
                          <div className="flex gap-2">
                            <select
                              value={testCountryCode}
                              onChange={(e) => setTestCountryCode(e.target.value)}
                              className="w-24 px-2 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-400 bg-white"
                              title="Country code"
                            >
                              {COUNTRY_CODES.map((c) => (
                                <option key={`${c.code}-${c.dialCode}`} value={c.dialCode}>
                                  {c.dialCode} ({c.code})
                                </option>
                              ))}
                            </select>
                            <div className="relative flex-1">
                              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                              <input
                                type="tel"
                                value={testPhoneNumber}
                                onChange={(e) =>
                                  setTestPhoneNumber(e.target.value.replace(/\D/g, ""))
                                }
                                placeholder="e.g., 9876543210"
                                className="w-full pl-10 pr-3 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-400"
                              />
                            </div>
                          </div>
                          {testPhoneNumber.trim() && (
                            <p className="text-[11px] text-gray-500 mt-1">
                              Sending to:{" "}
                              <span className="font-semibold text-gray-700">
                                {testCountryCode}
                                {testPhoneNumber.replace(/\D/g, "")}
                              </span>
                            </p>
                          )}
                        </div>

                        <button
                          onClick={handleSendTest}
                          disabled={isSendingTest || !testPhoneNumber.trim()}
                          className={`w-full px-4 py-2.5 text-sm font-semibold text-white rounded-xl flex items-center justify-center gap-2 transition-all ${
                            isSendingTest || !testPhoneNumber.trim()
                              ? "bg-gray-400 cursor-not-allowed"
                              : "bg-emerald-600 hover:bg-emerald-700"
                          }`}
                        >
                          {isSendingTest ? (
                            <>
                              <Loader className="w-4 h-4 animate-spin" />
                              Sending...
                            </>
                          ) : (
                            <>
                              <Send size={14} />
                              Send Test Message
                            </>
                          )}
                        </button>

                        {testResult && (
                          <div
                            className={`p-3 rounded-xl text-xs ${
                              testResult.success
                                ? "bg-green-50 border border-green-200 text-green-700"
                                : "bg-red-50 border border-red-200 text-red-700"
                            }`}
                          >
                            <div className="flex items-start gap-2">
                              {testResult.success ? (
                                <CheckCircle size={14} className="mt-0.5 flex-shrink-0" />
                              ) : (
                                <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
                              )}
                              <span className="font-medium">{testResult.message}</span>
                            </div>
                          </div>
                        )}

                        <p className="text-xs text-gray-500 font-medium">
                          Fill sample values, pick a country code and number, then send a
                          real test of the previewed template before launching the campaign.
                        </p>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center text-center py-12 px-4 rounded-xl bg-gray-50 border border-dashed border-gray-200 min-h-[320px]">
                  <div className="w-16 h-16 mb-4 rounded-2xl bg-white border border-gray-200 shadow-sm flex items-center justify-center">
                    <Eye className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-base font-bold text-gray-900 mb-2">No template selected</p>
                  <p className="text-sm text-gray-500 max-w-[240px]">
                    Pick one or more templates from the form to preview how your campaign messages will look
                  </p>
                </div>
              )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateCampaignForm;
